import './FacetListItem.scss';
import React from 'react';
import {useDispatch} from 'react-redux';
import {FacetValue, HibuEqualsFilter, HibuFilter} from '../../../../facet.types';
import {useIsFilterInverted, useIsFilterSelected} from '../../../../duck/filter.hooks';
import {changePendingState, selectFilter, unselectFilter} from '../../../../duck/filter.actions';
import {Checkbox} from '../../../../../common/checkbox/checkbox';
import {useLocalizationUtil} from '../../../../../localizationUtil';
import {filterFacets} from '../../../../facet.service';
import {useNavigate} from 'react-router-dom';
import {getIndentedDirPath} from '../../../../facet.utils';
import classNames from 'clsx';

type InputProps = {
    value: FacetValue;
    fieldName: string;
    selectedFilters: HibuFilter[];
    showCount: boolean;
};

const getTruncatableFacetText = (container: HTMLElement): HTMLElement | null =>
    container.querySelector<HTMLElement>('.subfolder-facet-text') ?? container.querySelector('label');

const isTextTruncated = (element: HTMLElement): boolean =>
    element.offsetWidth < element.scrollWidth;

export const FacetListItem = React.memo(({value, fieldName, selectedFilters, showCount}: InputProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {formatNumber} = useLocalizationUtil();
    const isSelected: boolean = useIsFilterSelected(value.filter as HibuEqualsFilter, selectedFilters);
    const isInverted: boolean = useIsFilterInverted(value.filter as HibuEqualsFilter, selectedFilters);

    const isFolderFacet = fieldName.endsWith('_dirPath');

    const onSelect = () => {
        dispatch(changePendingState(true));
        dispatch(selectFilter(value.filter, fieldName));
        dispatch(filterFacets(navigate)); // trigger search (fetchFacets and searchDocuments)
    };

    const onDeselect = () => {
        dispatch(changePendingState(true));
        dispatch(unselectFilter(value.filter, fieldName));
        dispatch(filterFacets(navigate)); // trigger search (fetchFacets and searchDocuments)
    };

    const onMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
        const facetElement = getTruncatableFacetText(event.currentTarget);
        if (facetElement && isTextTruncated(facetElement)) {
            event.currentTarget.title = value.name;
        } else {
            event.currentTarget.removeAttribute('title');
        }
    };

    return (
        <div
            className={classNames('facet', {'facet-selected': isSelected, 'inverted': isInverted})}
            onMouseEnter={onMouseEnter}
        >
            <div className="facet-value">
                <Checkbox value={isSelected} mayEdit={true}
                          labelKey={isFolderFacet ? getIndentedDirPath(value.name) : value.name}
                          onChange={(checkboxSelected: boolean) => {
                              checkboxSelected ? onSelect() : onDeselect();
                          }}
                          isRawHtml={isFolderFacet}
                >
                </Checkbox>
                {showCount &&
                    <span className="facet-count">{formatNumber(value.count)}</span>
                }
            </div>
        </div>
    );
});
